import { getWorkspaceRecords } from './http/workspace';
import type { FetchQueryOptions, MutationObserverOptions, QueryClient } from '@tanstack/query-core';
import type { UseQueryOptions } from '@tanstack/react-query';
import type { WorkspaceConfig, WorkspaceRecord } from '@thinkalpha/platform-ws-client/contracts/workspace.js';
import { ONE_HOUR } from '@thinkalpha/ui-core/util/timeConstants';
import type { ResourceQuery, ResourceQueryResponseWithMeta } from '@thinkalpha/ui-types/contracts/resource-query';
import type {
    NewWorkspace,
    UpdatedWorkspace,
    Workspace as LocalContractsWorkspace,
} from '@thinkalpha/ui-types/contracts/workspace';
import { createWorkspace, getWorkspaceBundle, getWorkspaceConfig, saveWorkspace } from 'src/lib/workspaces';
import type { LocalWorkspaceBundle } from 'src/lib/workspaces';
import { hydrateColumnTemplateByIdQuery } from 'src/queries/columnTemplates';
import { hydrateIndicatorByIdQuery } from 'src/queries/dictionary';
import { hydrateIdeaByIdQuery } from 'src/queries/ideas';
import { getWholeSlugMapQuery } from 'src/queries/slugMap';
import { getUniversesQuery, hydrateUniverseByIdQuery } from 'src/queries/universes';

// Constants
export const WORKSPACE = 'workspace';
export const CONFIG = 'config';
export const RECORDS = 'records';
export const BUNDLE = 'bundle';

export const getWorkspaceConfigQuery = (): FetchQueryOptions<WorkspaceConfig> => ({
    queryKey: [WORKSPACE, CONFIG],
    queryFn: () => getWorkspaceConfig(),
    staleTime: ONE_HOUR,
});

export const getWorkspaceBundleQuery = (id: 'default' | string): FetchQueryOptions<LocalWorkspaceBundle> => ({
    queryKey: [WORKSPACE, BUNDLE, id],
    queryFn: () => getWorkspaceBundle(id),
    gcTime: 0,
});

export function prefetchWorkspaceLoadDependencies(queryClient: QueryClient): void {
    void queryClient.prefetchQuery(getWholeSlugMapQuery());
    void queryClient.prefetchQuery(getUniversesQuery());
}

export function hydrateWorkspaceBundle(queryClient: QueryClient, bundle: LocalWorkspaceBundle): void {
    for (const idea of bundle.ideas) hydrateIdeaByIdQuery(queryClient, idea);
    for (const columnTemplate of bundle.columnTemplates) {
        hydrateColumnTemplateByIdQuery(queryClient, columnTemplate);
    }
    for (const universe of bundle.universes) hydrateUniverseByIdQuery(queryClient, universe);
    for (const indicator of bundle.indicators) hydrateIndicatorByIdQuery(queryClient, indicator);
}

/**
 * Get a single workspace record by ID
 */
export const getWorkspaceRecordByIdQuery = (id: string): UseQueryOptions<WorkspaceRecord> => {
    return {
        queryKey: [WORKSPACE, RECORDS, id],
        queryFn: async () => {
            const result = await getWorkspaceRecords({
                filter: `id eq '${id}'`,
                limit: 1,
            });
            if (!result.results?.[0]) {
                throw new Error(`Workspace with id ${id} not found`);
            }
            return result.results[0];
        },
    };
};

interface GetWorkspaceRecordsQueryOptions {
    resourceQueryOptions?: ResourceQuery;
    queryOptions?: Partial<FetchQueryOptions<ResourceQueryResponseWithMeta<WorkspaceRecord>>>;
}
/**
 * Get workspace records for a user
 *
 * @returns an object with queryKey, queryFn, and enabled
 */
export function getWorkspaceRecordsQuery(
    options: GetWorkspaceRecordsQueryOptions = {},
): FetchQueryOptions<ResourceQueryResponseWithMeta<WorkspaceRecord>> {
    const { resourceQueryOptions = {}, queryOptions = {} } = options;
    const workspaceQuerySearch = resourceQueryOptions.search ?? '';
    return {
        queryKey: [WORKSPACE, RECORDS, workspaceQuerySearch],
        queryFn: () =>
            getWorkspaceRecords({
                sortBy: 'createdAt desc',
                limit: 250,
                ...resourceQueryOptions,
            }),
        ...queryOptions,
    };
}

/**
 * Mutation for creating a new workspace (POST /workspaces).
 */
export function getCreateWorkspaceMutation(): MutationObserverOptions<
    LocalContractsWorkspace,
    Error,
    { workspace: NewWorkspace }
> {
    return {
        mutationFn: ({ workspace }) => createWorkspace(workspace),
    };
}

/**
 * Mutation for updating an existing workspace (PUT /workspaces/:id).
 */
export function getUpdateWorkspaceMutation(): MutationObserverOptions<
    LocalContractsWorkspace,
    Error,
    { workspace: UpdatedWorkspace }
> {
    return {
        mutationFn: ({ workspace }) => saveWorkspace(workspace, true),
    };
}
